class MainFaq extends HTMLElement {
  #controller = new AbortController();

  get #searchInput() {
    return this.querySelector("[data-faq-search]");
  }

  get #mobileSelect() {
    return this.querySelector("[data-faq-mobile-select]");
  }

  get #categoryButtons() {
    return this.querySelectorAll("[data-faq-category]");
  }

  get #items() {
    return this.querySelectorAll("[data-category]");
  }

  get #noResults() {
    return this.querySelector("[data-faq-no-results]");
  }

  #activeCategory = null;

  connectedCallback() {
    const { signal } = this.#controller;

    const firstButton = this.querySelector("[data-faq-category]");
    if (firstButton) {
      this.#activeCategory = firstButton.dataset.faqCategory;
    }

    const mobileSelect = this.#mobileSelect;
    if (mobileSelect) {
      if (!this.#activeCategory && mobileSelect.value) {
        this.#activeCategory = mobileSelect.value;
      }
      mobileSelect.addEventListener("change", this.#handleMobileSelect, { signal });
    }

    this.#searchInput?.addEventListener("input", this.#handleSearch, { signal });

    for (const button of this.#categoryButtons) {
      button.addEventListener("click", this.#handleCategoryClick, { signal });
    }

    this.#filterItems();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleMobileSelect = (event) => {
    this.#activeCategory = event.currentTarget.value;

    for (const btn of this.#categoryButtons) {
      btn.classList.remove("main-faq__category--active");
      btn.removeAttribute("aria-current");

      if (btn.dataset.faqCategory === this.#activeCategory) {
        btn.classList.add("main-faq__category--active");
        btn.setAttribute("aria-current", "true");
      }
    }

    this.#filterItems();
  };

  #handleCategoryClick = (event) => {
    const button = event.currentTarget;
    this.#activeCategory = button.dataset.faqCategory;

    for (const btn of this.#categoryButtons) {
      btn.classList.remove("main-faq__category--active");
      btn.removeAttribute("aria-current");
    }

    button.classList.add("main-faq__category--active");
    button.setAttribute("aria-current", "true");

    const mobileSelect = this.#mobileSelect;
    if (mobileSelect) {
      mobileSelect.value = this.#activeCategory;
    }

    if (this.#searchInput) {
      this.#searchInput.value = "";
    }

    this.#filterItems();
  };

  #handleSearch = () => {
    const query = this.#searchInput?.value.trim().toLowerCase() ?? "";

    if (query.length > 0) {
      this.#activeCategory = null;

      for (const btn of this.#categoryButtons) {
        btn.classList.remove("main-faq__category--active");
        btn.removeAttribute("aria-current");
      }
    }

    this.#filterItems(query);
  };

  /**
   * @param {string} [query]
   */
  #filterItems(query = "") {
    let visibleCount = 0;
    const visibleCategories = new Set();

    for (const item of this.#items) {
      const itemCategory = item.dataset.category;
      const questionEl = item.querySelector("[data-faq-question]");
      const questionText = questionEl?.textContent.toLowerCase() ?? "";

      let visible = true;

      if (query) {
        visible = questionText.includes(query);
      } else if (this.#activeCategory) {
        visible = itemCategory === this.#activeCategory;
      }

      item.hidden = !visible;

      if (visible) {
        visibleCount++;
        visibleCategories.add(itemCategory);
      }

      const details = item.querySelector("details");
      if (!visible && details) {
        details.open = false;
      }
    }

    if (query) {
      for (const btn of this.#categoryButtons) {
        const cat = btn.dataset.faqCategory;
        btn.style.display = visibleCategories.has(cat) ? "" : "none";
      }
    } else {
      for (const btn of this.#categoryButtons) {
        btn.style.display = "";
      }
    }

    const noResults = this.#noResults;
    if (noResults) {
      noResults.hidden = visibleCount > 0;
    }
  }
}

if (!customElements.get("main-faq")) {
  customElements.define("main-faq", MainFaq);
}
